import { useMutation, useQuery } from "@apollo/client/react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { Temporal } from "~/common/date"
import { calendarPath } from "~/common/paths"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { CALENDAR_DETAIL, MARK_POSTED, SCHEDULE } from "./calendar-queries"

export function PostSchedulePanel({ postId }: { postId: string }) {
  const { data, error, refetch } = useQuery(CALENDAR_DETAIL, {
    variables: { id: postId },
    pollInterval: 5000,
  })
  const [schedule, { loading: scheduling }] = useMutation(SCHEDULE)
  const [mark, { loading: marking }] = useMutation(MARK_POSTED)
  const [localTime, setLocalTime] = useState("")
  const [linkedinUrl, setLinkedinUrl] = useState("")
  const [actionError, setActionError] = useState<Error | null>(null)
  const post = data?.editorialWorkspace.post
  const timeZone = data?.experienceProfile?.timeZone ?? "America/New_York"
  if (!post) return error ? <GraphqlError error={error} /> : null
  return (
    <section
      className="space-y-4 rounded-2xl border bg-card p-5 sm:p-7"
      aria-label="Publishing plan"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Publishing plan</h3>
        <Link to={calendarPath({})} className="text-sm text-primary underline">
          My calendar
        </Link>
      </div>
      {post.status === "POSTED" ? (
        <>
          <p className="text-sm">
            Marked posted
            {post.postedAt
              ? ` · ${Temporal.Instant.from(post.postedAt).toZonedDateTimeISO(timeZone).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`
              : ""}
            .
          </p>
          {post.linkedinUrl && (
            <a
              href={post.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary underline"
            >
              View on LinkedIn
            </a>
          )}
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Choose a time for manual posting. Copy your text, download the clip, and publish on
            LinkedIn yourself. Mark it posted afterward.
          </p>
          {post.status !== "APPROVED" && (
            <p className="rounded-lg bg-secondary p-3 text-sm">
              Save and approve your post to schedule it.
            </p>
          )}
          {post.scheduledAt && (
            <p className="text-sm font-medium">
              Scheduled:{" "}
              {Temporal.Instant.from(post.scheduledAt)
                .toZonedDateTimeISO(post.scheduledTimeZone ?? timeZone)
                .toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}{" "}
              · {post.scheduledTimeZone}
            </p>
          )}
          {post.editable && post.status === "APPROVED" && (
            <>
              <label className="block text-sm">
                Posting time · {timeZone}
                <input
                  aria-label="Posting time"
                  type="datetime-local"
                  className="mt-2 block w-full rounded-md border bg-background p-3"
                  value={localTime}
                  onChange={(event) => setLocalTime(event.target.value)}
                />
              </label>
              <div className="flex flex-wrap gap-3">
                <Button
                  disabled={!localTime || scheduling}
                  onClick={async () => {
                    setActionError(null)
                    try {
                      const date = Temporal.PlainDateTime.from(localTime)
                        .toZonedDateTime(timeZone, { disambiguation: "reject" })
                        .toInstant()
                        .toString()
                      await schedule({
                        variables: {
                          input: {
                            id: post.id,
                            expectedVersion: post.lockVersion,
                            timeZone,
                            scheduledAt: date,
                          },
                        },
                      })
                      toast.success("Posting time saved")
                      await refetch()
                    } catch (err) {
                      setActionError(
                        err instanceof Error ? err : new Error("Could not schedule post")
                      )
                    }
                  }}
                >
                  Save posting time
                </Button>
                {post.scheduledAt && (
                  <Button
                    variant="outline"
                    disabled={scheduling}
                    onClick={async () => {
                      setActionError(null)
                      try {
                        await schedule({
                          variables: {
                            input: { id: post.id, expectedVersion: post.lockVersion, timeZone },
                          },
                        })
                        await refetch()
                        toast.success("Posting time removed")
                      } catch (err) {
                        setActionError(
                          err instanceof Error ? err : new Error("Could not remove time")
                        )
                      }
                    }}
                  >
                    Remove time
                  </Button>
                )}
              </div>
              <div className="space-y-3 border-t pt-4">
                <label className="block text-sm">
                  LinkedIn post URL (optional)
                  <input
                    type="url"
                    className="mt-2 block w-full rounded-md border bg-background p-3"
                    value={linkedinUrl}
                    onChange={(event) => setLinkedinUrl(event.target.value)}
                    placeholder="https://www.linkedin.com/feed/update/…"
                  />
                </label>
                <Button
                  variant="outline"
                  disabled={marking}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        "Have you published this post on LinkedIn? This records publication; it does not publish anything for you."
                      )
                    )
                      return
                    setActionError(null)
                    try {
                      await mark({
                        variables: {
                          input: {
                            id: post.id,
                            expectedVersion: post.lockVersion,
                            linkedinUrl: linkedinUrl || null,
                          },
                        },
                      })
                      await refetch()
                      toast.success("Marked posted")
                    } catch (err) {
                      setActionError(
                        err instanceof Error ? err : new Error("Could not mark posted")
                      )
                    }
                  }}
                >
                  I posted this on LinkedIn
                </Button>
              </div>
            </>
          )}
        </>
      )}
      {(actionError || error) && <GraphqlError error={(actionError || error)!} />}
    </section>
  )
}
